import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DocumentTemplateService } from '../../services/document-template/document-template.service';

import { ToastrService } from 'ngx-toastr';
import { KeyValuePair } from '../../../shared/models/key-value-pair';
import { catchError, of } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'app-template-share-dialog',
  templateUrl: './template-share-dialog.component.html',
  styleUrls: ['./template-share-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class TemplateShareDialogComponent implements OnInit {
  private static readonly MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB
  
  templateOptions: KeyValuePair[] = [];

  mode: 'select' | 'export' | 'import' = 'select';
  shareForm: FormGroup;
  isLoading = false;
  exportedData = '';
  selectedTemplateId: number | null = null;
  selectedTemplateName = '';
  importFile: File | null = null;

  constructor(
    private fb: FormBuilder,
    private documentTemplateService: DocumentTemplateService,
    private toastr: ToastrService,
    public activeModal: NgbActiveModal,
    private cdr: ChangeDetectorRef
  ) {
    this.shareForm = this.fb.group({
      selectedTemplate: ['', Validators.required],
      newTemplateName: ['', Validators.required]
    });
  }

  ngOnInit() {
    this.mode = 'select';
    this.loadTemplates();
  }

  private loadTemplates() {
    this.isLoading = true;
    
    this.documentTemplateService.getTemplates()
      .pipe(
        catchError((error) => {
          console.warn('Templates loading returned error (this is normal for accounts with no templates):', error);
          return of([]);
        }),
        untilDestroyed(this)
      )
      .subscribe({
        next: (templates) => {
          const templateList = templates || [];
          this.templateOptions = templateList.map(template => ({
            key: template.id?.toString() || '0',
            value: template.name || 'Unnamed Template'
          }));
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.templateOptions = [];
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      });
  }

  selectExport() {
    const templateId = this.shareForm.get('selectedTemplate')?.value;
    const template = this.templateOptions.find(t => t.key === templateId);
    
    if (templateId && template) {
      this.selectedTemplateId = templateId;
      this.selectedTemplateName = template.value;
      this.exportTemplate();
    } else {
      this.toastr.error('Please select a template to export');
    }
  }

  selectImport() {
    this.mode = 'import';
  }

  backToSelect() {
    this.mode = 'select';
    this.exportedData = '';
    this.importFile = null;
  }

  exportTemplate() {
    if (!this.selectedTemplateId) {
      return;
    }

    this.isLoading = true;
    this.documentTemplateService.exportTemplateAsJson(this.selectedTemplateId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (jsonData) => {
          this.exportedData = jsonData;
          this.isLoading = false;
          this.cdr.detectChanges();
          this.downloadTemplateFile();
        },
        error: () => {
          this.toastr.error('Failed to export template');
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      });
  }

  downloadTemplateFile() {
    if (!this.exportedData) {
      this.toastr.error('No template data available for download');
      return;
    }

    try {
      const blob = new Blob([this.exportedData], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const fileName = `${this.selectedTemplateName.replace(/[^a-z0-9]/gi, '_')}_template.json`;
      a.download = fileName;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      window.URL.revokeObjectURL(url);
      this.toastr.success('Template file downloaded.');
    } catch (error) {
      this.toastr.error('Failed to download template file');
    }
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file && file.type === 'application/json') {
      this.importFile = file;
    } else {
      this.toastr.error('Please select a valid JSON file');
    }
  }

  importTemplate() {
    if (!this.importFile) {
      this.toastr.error('Please select a template file');
      return;
    }

    const newTemplateName = this.shareForm.get('newTemplateName')?.value?.trim();
    if (!newTemplateName) {
      this.toastr.error('Please enter a template name');
      return;
    }

    if (this.importFile.size > TemplateShareDialogComponent.MAX_FILE_SIZE_BYTES) {
      this.toastr.error('File size too large. Please select a file smaller than 10MB.');
      return;
    }

    this.isLoading = true;
    this.cdr.detectChanges();
    
    const reader = new FileReader();
    
    reader.onerror = () => {
      this.toastr.error('Failed to read the selected file');
      this.isLoading = false;
      this.cdr.detectChanges();
    };
    
    reader.onload = (e) => {
      try {
        const templateData = e.target?.result as string;
        
        if (!templateData || templateData.trim().length === 0) {
          this.toastr.error('Selected file appears to be empty');
          this.isLoading = false;
          this.cdr.detectChanges();
          return;
        }

        this.documentTemplateService.importTemplateFromJson(newTemplateName, templateData)
          .pipe(untilDestroyed(this))
          .subscribe({
            next: (result) => {
              this.toastr.success(`Template "${newTemplateName}" imported successfully.`);
              this.activeModal.close(result);
            },
            error: () => {
              this.toastr.error('Failed to import template. Please check the template data format and that the template name is unique.');
              this.isLoading = false;
              this.cdr.detectChanges();
            }
          });
      } catch (error) {
        this.toastr.error('Invalid template file format. Please select a valid JSON file.');
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    };

    reader.readAsText(this.importFile);
  }

  onCancel() {
    this.activeModal.dismiss();
  }
}